import type { UnifiedCitation } from "@memorang/types/src/session/new-session";
import Box from "@memorang/ui/src/components/Box";
import { ChatUI } from "@memorang/ui/src/components/ChatUI/ChatUI";
import type {
	AiAssistantInfo,
	GetMessageActions,
	MenuAction,
} from "@memorang/ui/src/components/ChatUI/types";
import type { MessageData } from "@memorang/ui/src/components/ChatUI/types";
import type { TextInput } from "react-native";
import type { IconType } from "../../types";

type Props = {
	assistantInfo: AiAssistantInfo;
	messages: MessageData[];
	isRecommendedQuestionsFetching: boolean;
	loading?: boolean;
	streaming?: boolean;
	localRecommendedQueries: string[];
	handleSelectQuery: (query: string) => void;
	context: string;
	showReference?: boolean;
	handleNavigateToReference?: (url: string) => void;
	messageActions: GetMessageActions;
	referenceThumbnails?: Record<string, string>;
	isThreadLoading: boolean;
	inputQuery?: string;
	interceptSearchQuery: (query: string) => void;
	helperText: string;
	handleResetChatState: () => void;
	handleAbortSearch: () => void;
	messageFeedbackComponents: React.ReactNode;
	handleShowAllSources: (sources: UnifiedCitation[]) => void;
	handleOverlayClose: () => void;
	isItemAnswered?: boolean;
	initialTitle?: string;
	appBarFeedbackAction: MenuAction;
	shouldScrollViewScrollToEnd: boolean;
	textToSpeach: (runId: string, content: string) => React.ReactNode;
	loader?: React.ReactNode;
	font?: string;
	showMemorangPoweredBy?: boolean;
	bottomAppBarStyle?: React.ComponentProps<typeof Box>;
	inputRef?: React.RefObject<TextInput | null>;
	appbarIcon?: IconType;
};
const AssistantApplet = ({
	assistantInfo,
	messages,
	isRecommendedQuestionsFetching,
	handleShowAllSources,
	loading,
	streaming,
	localRecommendedQueries,
	handleSelectQuery,
	context,
	showReference,
	handleNavigateToReference,
	messageActions,
	referenceThumbnails,
	isThreadLoading,
	inputQuery,
	interceptSearchQuery,
	handleResetChatState,
	handleAbortSearch,
	messageFeedbackComponents,
	handleOverlayClose,
	isItemAnswered,
	initialTitle,
	appBarFeedbackAction,
	shouldScrollViewScrollToEnd,
	textToSpeach,
	loader,
	font,
	showMemorangPoweredBy,
	bottomAppBarStyle,
	inputRef,
	appbarIcon,
	helperText,
}: Props) => {
	return (
		<Box position="relative" height="100%">
			<ChatUI>
				<ChatUI.AppBar
					isBackButtonVisible={true}
					backAction={{
						icon: appbarIcon ?? "chevron-double-right",
						handleAction: handleOverlayClose,
					}}
					title={isItemAnswered ? initialTitle : assistantInfo.title}
					isSubtitleLoading={
						(isRecommendedQuestionsFetching && !initialTitle) || isThreadLoading
					}
					menuActions={[appBarFeedbackAction]}
				/>
				<ChatUI.ScrollView shouldScrollToEnd={shouldScrollViewScrollToEnd}>
					<ChatUI.MessageList
						assistantInfo={assistantInfo}
						messages={messages}
						isRecommendedQuestionsFetching={isRecommendedQuestionsFetching}
						handleShowAllSources={handleShowAllSources}
						loader={loader}
						loading={loading}
						streaming={streaming}
						recommendedQueries={localRecommendedQueries}
						handleSelectQuery={handleSelectQuery}
						context={context}
						handleNavigateToReference={handleNavigateToReference}
						showReference={showReference}
						messageActions={messageActions}
						referenceThumbnails={referenceThumbnails}
						isThreadLoading={isThreadLoading}
						textToSpeach={textToSpeach}
					/>
				</ChatUI.ScrollView>
				<ChatUI.BottomAppBar
					showConversationMode
					showInlineButtons
					searchPlaceholder={assistantInfo.alias}
					font={font}
					searchQuery={inputQuery ?? ""}
					interceptSearchQuery={interceptSearchQuery}
					searchContextMenu={helperText}
					handleReset={handleResetChatState}
					disableSearch={loading || streaming}
					handleAbortSearch={handleAbortSearch}
					showPartnersSection={showMemorangPoweredBy}
					style={bottomAppBarStyle}
					ref={inputRef}
				/>
			</ChatUI>
			{messageFeedbackComponents}
		</Box>
	);
};

export default AssistantApplet;
